import { useCallback, useRef, useState, type RefObject } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Separator } from "@design-system/Separator/Separator";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import type { CoaUndoCommand } from "./coaUndoCommand";
import {
    getBlockedUndoTooltip,
    getGroupedUndoModalQuestion,
    NO_SELECTION_UNDO_EXPLANATION,
    UNDO_MODAL_TITLE,
} from "./coaUndoCopy";
import {
    REVERTED_ACTIVITY_TOOLTIP,
    VIEW_REVERTED_ACTIVITY_LABEL,
} from "./coaRevertedActivityCopy";
import styles from "./CoaTemplateApplyHistoryDrawer.module.scss";

// Command island for the historical apply drawer. It mirrors the apply-preview
// island positioning (the preview shell owns insets). Close is always shown.
// A second action is added when there is something actionable:
//   - undo mode: "Undo selected (N)" for Applied wizard rows with ≥1
//     checkbox-capable entity. It is enabled only when every selected entity is
//     backend undo-eligible; otherwise it stays visible-but-disabled with a
//     hover/focus tooltip naming the blocked entities.
//   - reverted mode: "View reverted activity" cross-link, fired directly.
export const CoaTemplateApplyUndoFooter = ({
    onClose,
    undoCommand,
    onConfirmUndo,
    onViewReverted,
}: {
    onClose: () => void;
    undoCommand: CoaUndoCommand | null;
    onConfirmUndo: () => Promise<void>;
    onViewReverted: (() => void) | null;
}) => {
    const hasSecondaryAction = undoCommand !== null || onViewReverted !== null;
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const undoTriggerRef = useRef<HTMLButtonElement>(null);
    // A confirmed undo can leave nothing undoable, which unmounts the trigger
    // before the confirm handler resolves. Close is the one control that is
    // always here, so focus lands there instead of falling back to <body>.
    const restoreFocus = useCallback(() => {
        const trigger = undoTriggerRef.current;
        if (trigger?.isConnected) {
            trigger.focus();
            return;
        }
        closeButtonRef.current?.focus();
    }, []);
    return (
        <CommandIsland alignEnd>
            <Button
                ref={closeButtonRef}
                variant="secondary"
                size="md"
                onClick={onClose}
            >
                Close
            </Button>
            {hasSecondaryAction && (
                <Separator
                    orientation="vertical"
                    className={styles.commandIslandSeparator}
                />
            )}
            {onViewReverted && (
                <ViewRevertedAction
                    onViewReverted={onViewReverted}
                    // The shortcut belongs to Undo whenever it is offered, so
                    // mod+enter never has two possible meanings.
                    shortcut={undoCommand ? "none" : "modEnter"}
                />
            )}
            {undoCommand && (
                <UndoAction
                    command={undoCommand}
                    onConfirmUndo={onConfirmUndo}
                    triggerRef={undoTriggerRef}
                    restoreFocus={restoreFocus}
                />
            )}
        </CommandIsland>
    );
};

// An unavailable Undo stays a real, focusable button carrying `aria-disabled`
// instead of a native `disabled` one: a natively disabled button leaves the tab
// order, so a keyboard user can never reach the only control that explains why
// undo can't run. The click handler is guarded instead.
const UndoAction = ({
    command,
    onConfirmUndo,
    triggerRef,
    restoreFocus,
}: {
    command: CoaUndoCommand;
    onConfirmUndo: () => Promise<void>;
    triggerRef: RefObject<HTMLButtonElement | null>;
    restoreFocus: () => void;
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        // Focus goes back to the trigger when it is still mounted, and to the
        // footer's fallback when a successful undo removed it.
        restoreFocus();
    }, [restoreFocus]);
    const openModal = useCallback(() => {
        if (!command.canConfirm) {
            return;
        }
        setIsModalOpen(true);
    }, [command.canConfirm]);
    const handleConfirm = useCallback(async () => {
        await onConfirmUndo();
        closeModal();
    }, [closeModal, onConfirmUndo]);

    // The count reflects selected entities, not eligible items — the mutation
    // only runs on confirm, so the shortcut just opens the modal.
    const label = `Undo selected (${command.selectedEntityCount})`;
    const unavailableReason = getUndoUnavailableReason(command);

    useKeyboardCommand({
        keys: "mod+enter",
        callback: openModal,
        options: { description: label },
    });

    return (
        <>
            {/* Kept mounted and toggled with `disabled`: a Tooltip with no
                content renders the bare button, so a changing reason would
                remount it and drop keyboard focus. */}
            <Tooltip
                content={unavailableReason ?? label}
                disabled={unavailableReason === undefined}
            >
                <Button
                    ref={triggerRef}
                    variant="primary"
                    size="md"
                    type="button"
                    className={
                        unavailableReason ? styles.undoUnavailable : undefined
                    }
                    aria-disabled={!command.canConfirm}
                    aria-label={
                        unavailableReason
                            ? `${label}. ${unavailableReason}`
                            : undefined
                    }
                    onClick={openModal}
                    rightIcon={
                        <span data-slot-box="">
                            <Shortcut keys="mod+enter" />
                        </span>
                    }
                >
                    {label}
                </Button>
            </Tooltip>
            {isModalOpen && (
                <ConfirmationModal
                    show
                    title={UNDO_MODAL_TITLE}
                    question={getGroupedUndoModalQuestion({
                        selectedEntityCount: command.selectedEntityCount,
                        eligibleActionCount: command.eligibleActionCount,
                    })}
                    yes="Undo"
                    yesBtnVariant="primary"
                    no="Cancel"
                    noBtnVariant="secondary"
                    onHide={closeModal}
                    onReject={closeModal}
                    onConfirm={handleConfirm}
                />
            )}
        </>
    );
};

// Why the Undo command can't run, or undefined when it can. A pending mutation
// needs no explanation, so the tooltip identity stays stable while it runs.
function getUndoUnavailableReason(command: CoaUndoCommand): string | undefined {
    if (command.canConfirm) {
        return undefined;
    }
    if (command.selectedEntityCount === 0) {
        return NO_SELECTION_UNDO_EXPLANATION;
    }
    if (
        command.blockedEntityNames.length > 0 &&
        command.blockedReasonCategory
    ) {
        return getBlockedUndoTooltip(
            command.blockedEntityNames,
            command.blockedReasonCategory,
        );
    }
    return undefined;
}

const ViewRevertedAction = ({
    onViewReverted,
    shortcut,
}: {
    onViewReverted: () => void;
    shortcut: "modEnter" | "none";
}) => {
    if (shortcut === "none") {
        return (
            <ButtonWithLoader
                variant="primary"
                size="md"
                type="button"
                loading={false}
                tooltip={REVERTED_ACTIVITY_TOOLTIP}
                onClick={onViewReverted}
            >
                {VIEW_REVERTED_ACTIVITY_LABEL}
            </ButtonWithLoader>
        );
    }
    return (
        <ButtonWithLoader
            variant="primary"
            size="md"
            type="button"
            loading={false}
            tooltip={REVERTED_ACTIVITY_TOOLTIP}
            hotKey="mod+enter"
            hotKeyOptions={{
                description: VIEW_REVERTED_ACTIVITY_LABEL,
                preventDefault: true,
            }}
            hotKeyDisplayPosition="inline"
            onClick={onViewReverted}
        >
            {VIEW_REVERTED_ACTIVITY_LABEL}
        </ButtonWithLoader>
    );
};
